import { inject, Injectable } from '@angular/core';
import { HttpClient, HttpResponse } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { map, catchError } from 'rxjs/operators';

import { Credentials, CredentialsService } from './credentials.service';
import { LoginResponse, LoginSuccess, MfaRequired, MfaSetupRequired } from './mfa.service';
import { MessageServiceBroker } from '../../services/message.service';
import { ODataError } from 'src/app/@shared/utilities/odata/response';
import { ODataMessage } from 'src/app/@shared/utilities/odata/message';

import { Logger } from '../../services/logger/logger.service';
import { AppService } from '../../services/app.service';

export interface ChangePasswordContext {
    oldPassword: string;
    password: string;
    confirmPassword: string;
}

export interface LoginContext {
    email: string;
    password: string;
    remember?: boolean;
}

export interface RegistrationContext {
    firstName: string;
    lastName: string;
    email: string;
    password: string;
    confirmPassword: string;
}

export interface ResetPasswordContext {
    password: string;
    confirmPassword: string;
    user_id: string,
    timestamp: string,
    signature: string,
}

const LOGGER = new Logger('AuthenticationService');

@Injectable({
    providedIn: 'root'
})
export class AuthenticationService {
    private _appService = inject(AppService);
    private _credentialsService = inject(CredentialsService);
    private _httpClient = inject(HttpClient);
    private _messageService = inject(MessageServiceBroker);


    /**
     * Change le mot de passe de l'utilisateur connecté.
     *
     * Depuis #1214, le serveur révoque à cette occasion tous les refresh tokens du
     * compte **sauf celui du cookie porté par la requête** : c'est ce cookie qui désigne
     * la session courante. Sans `withCredentials`, la session qui vient de changer le mot
     * de passe tomberait avec les autres au prochain rafraîchissement.
     */
    changePassword$(context: ChangePasswordContext): Observable<boolean | ODataError> {
        return this._httpClient
            .post(`/registration/change-password/`, {
                old_password: context.oldPassword,
                password: context.password,
                password_confirm: context.confirmPassword,
            }, { withCredentials: true })
            .pipe(
                map((_: any) => {
                    return true;
                }),
                catchError(err => { 
                    return this._registrationError(err);
                })
            );
    }

    getQrCode$(): Observable<string> {
        // Le refresh token est désormais dans un HttpOnly cookie inaccessible en JS.
        // Cette méthode doit être réimplémentée via un endpoint backend qui génère le QR code.
        throw new Error('getQrCode$() doit être réimplémenté côté backend (le refresh token est en HttpOnly cookie).');
    }

    /**
     * Authentifie l'utilisateur
     * Demande le token de connexion qui sera ensuite utilisé pour toutes les requêtes
     * @param context Les paramètres de connexion.
     * @return Les données d'authentification de l'utilisateur.
     */
    login$(context: LoginContext): Observable<LoginResponse> {
        return this._httpClient
            .post(`/token/`, {
                email: context.email,
                password: context.password,
                remember: context.remember
            }, { withCredentials: true, observe: 'response' })
            .pipe(
                map((response: HttpResponse<any>) => {
                    const body = response.body;
                    if (response.status === 206) {
                        if (body.mfaRequired) {
                            return { type: 'mfa_required', preAuthToken: body.preAuthToken } as MfaRequired;
                        } else {
                            return { type: 'mfa_setup_required', preAuthToken: body.preAuthToken } as MfaSetupRequired;
                        }
                    }
                    const tokenData = { access: body.access };
                    this._credentialsService.setCredentials(tokenData);
                    return { type: 'success', credentials: tokenData } as LoginSuccess;
                }),
                catchError(err => {
                    let error = ODataError.fromResponse(err);
                    this._messageService.sendODataMessages(error.messages);
                    throw error;
                })
            );
    }

    /**
     * Logs out the user and clear credentials.
     * Appelle le backend pour supprimer le HttpOnly cookie de refresh token.
     * @return True if the user was logged out successfully.
     */
    logout$(): Observable<boolean> {
        LOGGER.debug('Déconnexion...');
        const wasLoggedIn = !!this._credentialsService.credentials?.access;
        return this._httpClient.post(`/token/logout/`, {}, { withCredentials: true }).pipe(
            catchError(() => of(false)), // Ne pas bloquer la déconnexion si l'API est indisponible
            map(() => {
                this._credentialsService.setCredentials();
                LOGGER.debug('Ré-initialisation des données de l\'application');
                this._appService.reset();
                LOGGER.debug('Tata bisou!');
                return wasLoggedIn;
            })
        );
    }

    /**
     * Création du compte utilisateur
     * @param context Les paramètres de connexion.
     * @return Les données d'authentification de l'utilisateur.
     */
    register$(context: RegistrationContext): Observable<boolean | ODataError> {
        return this._httpClient
            .post(`/registration/register/`, {
                firstName: context.firstName,
                lastName: context.lastName,
                email: context.email,
                username: context.email,
                password: context.password,
                password_confirm: context.confirmPassword
            })
            .pipe(
                map((_: any) => {
                    return true;
                }),
                catchError(err => {
                    return this._registrationError(err);
                })
            );
    }

    /**
     * Demande un nouveau token d'accès via le HttpOnly cookie de refresh token.
     * Le cookie est envoyé automatiquement par le navigateur.
     */
    refreshToken$(): Observable<Credentials | ODataError> {
        return this._httpClient
            .post(`/token/refresh/`, {}, { withCredentials: true })
            .pipe(
                map((body: any) => {
                    let tokenData = { access: body.access };
                    this._credentialsService.setCredentials(tokenData);
                    return tokenData;
                }),
                catchError(err => {
                    this._credentialsService.setCredentials();
                    let error = ODataError.fromResponse(err);
                    this._messageService.sendODataMessages(error.messages);
                    throw error;
                })
            );
    }

    /**
     * Demande l'envoi du lien de validation d'un changement d'adresse email.
     *
     * Deux branches manquaient ici, et l'écran de profil restait muet sur les deux
     * (#1124). Le succès n'annonçait pas que tout se joue maintenant dans la **nouvelle**
     * boîte — sans quoi le changement n'a pas lieu —, et le refus n'existait pas : la
     * route répondait 200 sur une adresse déjà rattachée à un compte, sans rien envoyer.
     * Elle répond désormais 400 `EMAIL_CHANGE_ALREADY_REGISTERED`, que le formulaire
     * affiche sous le champ d'adresse — pas de toast, l'erreur porte sur une saisie.
     */
    registerEmail$(email: string): Observable<any> {
        return this._httpClient
            .post(`/registration/register-email/`, {
                email: email
            })
            .pipe(
                map((response: any) => {
                    this._messageService.sendODataMessages([new ODataMessage({
                        domain: 'user',
                        longText: `Ouvrez le message envoyé à ${email} et cliquez sur son lien pour confirmer le changement. `
                            + `Tant que ce n'est pas fait, votre adresse de connexion reste inchangée.`,
                        shortText: 'Validez votre nouvelle adresse',
                        type: 'I'
                    })]);
                    return response;
                }),
                catchError(err => {
                    throw ODataError.fromResponse(err);
                })
            );
    }

    /**
     * Renouvelle le mot de passe à partir du lien reçu par email.
     *
     * Depuis #1123, les refus se lisent à deux endroits distincts de la réponse, et les
     * confondre est l'erreur à ne pas commettre. Le **lien** refusé porte un code du
     * référentiel — `RESET_PASSWORD_LINK_EXPIRED`, `RESET_PASSWORD_LINK_INVALID`,
     * `RESET_PASSWORD_LINK_INCOMPLETE` — que le formulaire affiche en bannière, à la place
     * de ses champs : il n'y a plus rien à ressaisir, la sortie est « Mot de passe oublié ? ».
     * Le **mot de passe** refusé, lui, reste une erreur de champ sans code, affichée sous
     * le champ concerné.
     *
     * Le serveur vérifie désormais le lien avant le mot de passe : un lien mort et un mot
     * de passe trop faible ne coûtent plus deux allers-retours.
     */
    resetPassword$(context: ResetPasswordContext): Observable<boolean | ODataError> {
        return this._httpClient
            .post(`/registration/reset-password/`, {
                password: context.password,
                password_confirm: context.confirmPassword,
                user_id: context.user_id,
                timestamp: context.timestamp,
                signature: context.signature,
            })
            .pipe(
                map((_: any) => {
                    return true;
                }),
                catchError(err => {
                    return this._registrationError(err);
                })
            );
    }

    sendResetPasswordLink$(email: string): Observable<any> {
        return this._httpClient
            .post(`/registration/send-reset-password-link/`, {
                email: email
            });
    }

    /**
     * Valide un changement d'adresse email à partir du lien reçu sur la nouvelle adresse.
     *
     * Les refus portent un code du référentiel depuis #1124 —
     * `EMAIL_VERIFICATION_LINK_EXPIRED`, `EMAIL_VERIFICATION_LINK_INVALID`,
     * `EMAIL_VERIFICATION_LINK_INCOMPLETE`, `EMAIL_VERIFICATION_USER_NOT_FOUND`,
     * `EMAIL_VERIFICATION_ALREADY_REGISTERED`. Ils ne passent volontairement par aucune
     * notification : l'écran de validation les affiche lui-même en bannière, avec le texte
     * du serveur. Les cinq ramènent au profil, mais le geste qui y attend n'est pas le
     * même — redemander le lien pour quatre d'entre eux, choisir une AUTRE adresse pour
     * `EMAIL_VERIFICATION_ALREADY_REGISTERED`. C'est le texte du référentiel qui porte
     * cette nuance, d'où l'interdiction de le recopier ici.
     */
    verifyEmail$(payload: any): Observable<any> {
        return this._httpClient
            .post(`/registration/verify-email/`, payload)
            .pipe(
                catchError(err => {
                    throw ODataError.fromResponse(err);
                })
            );
    }

    /**
     * Valide un compte à partir du lien reçu par email à l'inscription.
     *
     * Les refus portent un code du référentiel depuis #1111 —
     * `REGISTRATION_LINK_EXPIRED`, `REGISTRATION_LINK_INVALID`,
     * `REGISTRATION_LINK_INCOMPLETE`, `REGISTRATION_NOT_FOUND`. Ils ne passent
     * volontairement par aucun toast : l'écran de validation les affiche lui-même en
     * bannière, parce que le geste de sortie est le même pour les quatre — reprendre
     * l'inscription, qui renvoie un lien neuf.
     */
    verifyUser$(payload: any): Observable<any> {
        return this._httpClient
            .post(`/registration/verify-registration/`, payload)
            .pipe(
                catchError(err => {
                    throw ODataError.fromResponse(err);
                })
            );
    }

    private _registrationError(err: any): Observable<boolean | ODataError> {
        let detail: ODataMessage;
        // Refus nommés par le serveur (#1123) : ils sont lus AVANT que le canal ne soit
        // vidé, et réinstallés plus bas. Ce vidage sans lecture préalable faisait retomber
        // tous les refus de lien de renouvellement sur « Erreur inconnue » : le symptôme
        // d'origine du ticket.
        const serverMessages: any[] = Array.isArray(err?.error?.messages) ? err.error.messages : [];
        err.error.messages = [];
        err.error.errors = {};
        let showMessage: boolean = true;

        // Utilisateur existant
        if (!!err?.error?.email && err?.error?.email[0] === 'Un objet utilisateur avec ce champ email existe déjà.') {
            detail = new ODataMessage({
                domain: 'user',
                longText: `Un utilisateur avec cette adresse email existe déjà.`,
                shortText: 'Utilisateur existant',
                type: 'E'
            });
            err.error.messages.push(detail);
            err.error.errors.email = detail;
        }

        // Mot de passe invalide
        if (!!err?.error?.password) {
            for (let i = 0; i < err.error.password.length; i++) {
                detail = new ODataMessage({
                    domain: 'user',
                    longText: err.error.password[i],
                    shortText: 'Mot de passe invalide',
                    type: 'E'
                });
                err.error.messages.push(detail);
                err.error.errors.password = detail;
            }
        }

        // Mots de passe différents
        if (!!err?.error?.passwordConfirm && err?.error?.passwordConfirm[0] === `Passwords don't match`) {
            detail = new ODataMessage({
                domain: 'user',
                longText: `Les mots de passe ne sont pas identiques.`,
                shortText: 'Mot de passe invalide',
                type: 'E'
            });
            err.error.messages.push(detail);
            err.error.errors.password = detail;
            err.error.errors.confirmPassword = detail;
        }

        // Plafond de débit atteint (#1059) : 5 appels / heure par IP, partagés avec la demande
        // de réinitialisation. Pas de toast : le formulaire reconnaît le statut 429 et affiche
        // ce message en bannière explicative.
        if (err.status === 429) {
            detail = new ODataMessage({
                domain: 'user',
                longText: `Le nombre de créations de compte autorisées depuis votre connexion internet a été atteint pour l'heure. `
                    + `Réessayez dans une heure. Si vous devez inscrire plusieurs collaborateurs d'un coup, contactez le support Biifor `
                    + `à <a href="mailto:contact@biifor.com">contact&#64;biifor.com</a>.`,
                shortText: 'Trop de tentatives',
                type: 'E'
            });
            err.error.messages.push(detail);
            showMessage = false;
        }

        // Le référentiel serveur fait foi dès qu'il a nommé le refus : ses messages
        // remplacent les règles locales ci-dessus, et ne partent pas en notification —
        // l'écran les affiche lui-même en bannière, avec la sortie qui va avec.
        if (serverMessages.length > 0) {
            err.error.messages = serverMessages;
            showMessage = false;
        }

        // Message par défaut
        if (err.error.messages.length === 0) {
            detail = new ODataMessage({
                domain: 'user',
                longText: `Une erreur est survenue, veuillez réessayer ultérieurement.`,
                shortText: 'Erreur inconnue',
                type: 'E'
            });
            err.error.messages.push(detail);
            showMessage = true;
        }

        let error = ODataError.fromResponse(err);
        if (showMessage) {
            this._messageService.sendODataMessages(error.messages);
        }
        throw error;
    }
}
